import React, { createContext, useState, useEffect } from 'react';
import axios from 'axios';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is authenticated on component mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setLoading(false);
        return;
      }
      
      try {
        // Add timeout configuration to prevent request aborted errors
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/auth/user`, {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          timeout: 10000 // 10 seconds timeout
        });
        
        console.log('CheckAuth response:', response.data);
        
        // Handle different response structures
        if (response.data.data) {
          setUser(response.data.data);
        } else {
          setUser(response.data);
        }
        
        setIsAuthenticated(true);
        setError(null);
      } catch (err) {
        console.error('Authentication error:', err);
        localStorage.removeItem('token');
        setError('Authentication failed. Please log in again.');
      } finally {
        setLoading(false);
      }
    };
    
    checkAuth();
  }, []);

  // Login function
  const login = async (email, password) => {
    setLoading(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/auth/login`, {
        email,
        password
      });
      
      console.log('Login response:', response.data);
      
      // Handle nested response structure from the backend
      if (response.data.data) {
        // Nested structure: { data: { user: {...}, token: '...' } }
        localStorage.setItem('token', response.data.data.token);
        localStorage.setItem('user_id', response.data.data.user.id);
        setUser(response.data.data.user);
      } else {
        // Flat structure: { user: {...}, token: '...' }
        localStorage.setItem('token', response.data.token);
        setUser(response.data.user);
      }
      
      setIsAuthenticated(true);
      setError(null);
      return true;
    } catch (err) {
      console.error('Login error:', err);
      setError(err.response?.data?.message || 'Login failed. Please check your credentials.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    const token = localStorage.getItem('token');
    
    if (token) {
      try {
        // Call the backend to invalidate the token
        await axios.post(`${process.env.REACT_APP_API_URL}/auth/logout`, {}, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      } catch (err) {
        console.error('Logout error:', err);
      }
    }
    
    // Clear local state regardless of API call success
    localStorage.removeItem('token');
    localStorage.removeItem('user_id');
    setUser(null);
    setIsAuthenticated(false);
  };

  // Register function
  const register = async (name, email, password, password_confirmation) => {
    setLoading(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/auth/register`, {
        name,
        email,
        password,
        password_confirmation
      });
      
      localStorage.setItem('token', response.data.token);
 
      setUser(response.data.user);
      setIsAuthenticated(true);
      setError(null);
      return true;
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      isAuthenticated, 
      loading, 
      error, 
      login, 
      logout, 
      register 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
